import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { User } from "../../pages/playground/types";

interface UserListProps {
  users: User[];
  onAddUser: () => void;
  onEditUser: (user: User) => void;
  canManageUsers: boolean;
}

/**
 * UserList component displays a table of users with their details and actions
 * Includes add/edit/delete functionality and role-specific management buttons
 */
export const UserList: React.FC<UserListProps> = ({
  users,
  onAddUser,
  onEditUser,
  canManageUsers,
}) => {
  // Helper to get color based on role
  const getRoleColor = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "error";
      case "MANAGER":
        return "warning";
      case "CLIENT":
        return "success";
      case "COPYWRITER":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {canManageUsers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddUser}
          >
            New user
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{
                  opacity: user.disabled ? 0.6 : 1,
                  backgroundColor: user.disabled ? "#fafafa" : "inherit",
                }}
              >
                <TableCell>{`${user.name || ""} ${
                  user.surname || ""
                }`}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip label={user.role} color={getRoleColor(user.role)} />
                </TableCell>
                <TableCell>
                  {user.disabled ? (
                    <Chip label="Disabled" size="small" color="error" />
                  ) : (
                    <Chip label="Active" size="small" color="success" />
                  )}
                </TableCell>
                <TableCell>
                  {canManageUsers && (
                    <IconButton onClick={() => onEditUser(user)}>
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
